import {
  HHAlert,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { InputAdornment } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useDynamicDrawerContext } from '../../../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import {
  GetBillsDocument,
  useProcessCopaymentMutation,
} from '../../../../types';
import {
  checkCopayEligibleFromListData,
  CopayEligible,
  getCopayHelperText,
  getMaxCopayAmount,
} from '../../../../utils/bills-utils';
import {
  ActionTypeDisplayText,
  GridLevelName,
} from '../../../grid-components/grid-types';
import { PanelAlert } from '../../../grid-components/panel-alert';
import { PanelLayout } from '../../../grid-components/panel-layout';
import { PanelTitle } from '../../../grid-components/panel-title';
import { noteInvalid } from '../../../grid-components/panel-validation';
import { BillTypeDataList } from '../../bill-grid-components/bill-management-types';
import { BillPanelProps } from '../panel-types';
import { SelectedBillsGrid, SelectedGridColumnId } from './selected-grid';

const COPAYMENT_COLUMNS: SelectedGridColumnId[] = [
  'billId',
  'billAmount',
  'costShareStatus',
  'dateOfService',
  'client',
  'insurer',
];

export const ProcessCopaymentPanel = ({
  selectedRows,
  clients,
  insurers,
  onClearSelections,
}: BillPanelProps): JSX.Element => {
  const { setIsOpen, setTitle } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const [panelSelectedRows, setPanelSelectedRows] =
    useState<BillTypeDataList>(selectedRows);
  const [note, setNote] = useState<string>('');
  const [copayAmount, setCopayAmount] = useState<number | null>(null);

  const [processCopayment, { loading }] = useProcessCopaymentMutation();

  const copayEligibleMap = useMemo(() => {
    const results = new Map<number, CopayEligible>();

    panelSelectedRows.forEach(row => {
      results.set(row.billId, checkCopayEligibleFromListData(row));
    });

    return results;
  }, [panelSelectedRows]);

  const eligibleRows = panelSelectedRows.filter(
    row => copayEligibleMap.get(row.billId)?.eligible === true,
  );

  const ineligibleRows = panelSelectedRows.filter(
    row => copayEligibleMap.get(row.billId)?.eligible === false,
  );

  const maxCopayAmount = getMaxCopayAmount(copayEligibleMap);
  const isCopayValid =
    copayAmount !== null && copayAmount > 0 && copayAmount <= maxCopayAmount;

  const isInvalid =
    noteInvalid(note) ||
    !isCopayValid ||
    eligibleRows.length === 0 ||
    ineligibleRows.length > 0;

  const handleSubmit = async (): Promise<void> => {
    try {
      await processCopayment({
        variables: {
          processCopaymentInput: {
            billIds: eligibleRows.map(row => row.billId),
            copayAmount: copayAmount as number,
          },
        },
        refetchQueries: [GetBillsDocument],
      });
      setSnackbarState(
        `Copayment processing started for ${eligibleRows.length} bill(s)`,
        'success',
        5000,
      );
      onClearSelections();
      setTimeout(() => setIsOpen(false), 500);
    } catch (e) {
      setSnackbarState(
        `Failed to process copayment with error: ${e instanceof Error ? e.message : 'Unknown error'}`,
        'error',
        5000,
      );
    }
  };

  const handleSelectionChanged = useCallback(
    (selectedGridRows: BillTypeDataList): void => {
      setPanelSelectedRows(selectedGridRows);
      setTitle(
        <PanelTitle
          actionType={ActionTypeDisplayText.PROCESS_COPAYMENT}
          rowCount={selectedGridRows.length}
          gridLevel={GridLevelName.BILL}
        />,
      );
    },
    [setTitle],
  );

  return (
    <PanelLayout
      selectedGrid={
        <HHStack direction="column" spacing={2} sx={{ flex: 1 }}>
          <SelectedBillsGrid
            selectedRows={panelSelectedRows}
            setPanelSelectedRows={handleSelectionChanged}
            clients={clients}
            insurers={insurers}
            columns={COPAYMENT_COLUMNS}
            extraRowData={copayEligibleMap}
          />
          {ineligibleRows.length > 0 && (
            <HHStack padding={2}>
              <HHAlert
                severity="info"
                title="Ineligible for Copayment"
                hhVariant="outlined"
              >
                <HHTypography variant="body2" hhVariant="body2">
                  {ineligibleRows.length} bill(s) are not eligible for copayment
                  processing:
                </HHTypography>
                {ineligibleRows.map(row => (
                  <HHTypography
                    key={row.billId}
                    variant="body2"
                    hhVariant="body2"
                  >
                    Bill {row.billId}:{' '}
                    {copayEligibleMap.get(row.billId)?.reason}
                  </HHTypography>
                ))}
              </HHAlert>
            </HHStack>
          )}
        </HHStack>
      }
      alert={<PanelAlert drawerMode="process-copayment" />}
      additionalInputs={
        <HHTextField
          data-testid="copay-amount-input"
          hhVariant="variant-bypass"
          label="Copay Amount"
          fullWidth
          type="number"
          value={copayAmount ?? ''}
          onChange={(e): void => {
            const val = e.target.value;
            setCopayAmount(val === '' ? null : parseFloat(val));
          }}
          error={copayAmount !== null && !isCopayValid}
          helperText={getCopayHelperText(copayAmount, maxCopayAmount)}
          disabled={eligibleRows.length === 0}
          inputProps={{
            inputMode: 'decimal',
            step: '0.01',
            name: 'copayAmount',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                disableTypography
                sx={{ color: 'gray' }}
              >
                $
              </InputAdornment>
            ),
          }}
        />
      }
      note={note}
      setNote={setNote}
      setIsOpen={setIsOpen}
      isSubmitting={loading}
      isInvalid={isInvalid}
      actionLabel="Process Copayment"
      handleSubmit={handleSubmit}
    />
  );
};
